import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Back-swipe guard — on mobile, an accidental edge-swipe (browser back
 * gesture) pops the /report route and dumps the user on the landing page,
 * losing the whole analysis ("przesunę palcem i cofa mnie i od nowa trzeba
 * wszystko" — user complaint Jun 2026). While a report/verdict is open we
 * push a sentinel history entry and absorb the first back; a second back
 * within 2.5s is treated as intentional and lets the user leave (standard
 * Android double-back-to-exit pattern).
 *
 * @param {boolean} active  arm the guard (truthy while a report is on screen)
 * @param {function} t      translation lookup for the toast copy
 */
export const useBackSwipeGuard = (active, t) => {
  const armedRef = useRef(false);
  const lastPopRef = useRef(0);

  // history.pushState/back can throw in sandboxed iframes / some webviews —
  // the guard is best-effort UX sugar, so degrade silently with a debug trace.
  const logHistoryError = (context, err) => {
    try { console.debug(`[backSwipeGuard] ${context}:`, err); } catch { /* console unavailable */ }
  };

  useEffect(() => {
    if (!active) {
      armedRef.current = false;
      return undefined;
    }
    if (!armedRef.current) {
      armedRef.current = true;
      try {
        window.history.pushState({ worthifyBackGuard: true }, document.title, window.location.href);
      } catch (e) { logHistoryError("arm pushState failed", e); }
    }
    const onPopState = () => {
      if (!armedRef.current) return;
      const now = Date.now();
      if (now - lastPopRef.current < 2500) {
        // Second back in quick succession — intentional exit, let it through.
        armedRef.current = false;
        try { window.history.back(); } catch (e) { logHistoryError("history.back failed", e); }
        return;
      }
      lastPopRef.current = now;
      // Re-arm: push the sentinel back so the report stays on screen.
      try {
        window.history.pushState({ worthifyBackGuard: true }, document.title, window.location.href);
      } catch (e) { logHistoryError("re-arm pushState failed", e); }
      toast(t("backGuardToast") || "Swipe back again to leave this report", { duration: 2200 });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [active, t]);
};

export default useBackSwipeGuard;
